import filter from "lodash/filter";
import includes from "lodash/includes";
import isEqual from "lodash/isEqual";
import map from "lodash/map";
import uniq from "lodash/uniq";
import { ROOT_COLUMN_KEY } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/calendar_content/dual_scrollable_grid/constants";
import { ColumnsDisplay, ColumnsType } from "PFApp/booking/store";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { TableColumn } from "PFTypes";

export enum ColumnDisplayMode {
  Hidden = "hidden",
  Table = "table",
  Inline = "inline"
}

export const getColumnDisplayMode = (name: string, display: ColumnsDisplay): ColumnDisplayMode => {
  if (includes(display.selectedColumns, name)) {
    return ColumnDisplayMode.Table;
  }
  if (includes(display.inlineColumns, name)) {
    return ColumnDisplayMode.Inline;
  }
  return ColumnDisplayMode.Hidden;
};

export const changeColumnDisplayMode = (
  display: ColumnsDisplay,
  column: TableColumn,
  mode: ColumnDisplayMode
): ColumnsDisplay => {
  // MultiToggle fires onChange for the already selected option too; re-appending would move it to the end
  if (column.locked || getColumnDisplayMode(column.name, display) === mode) {
    return display;
  }
  const selectedColumns = filter(display.selectedColumns, (name) => name !== column.name);
  const inlineColumns = filter(display.inlineColumns, (name) => name !== column.name);
  return {
    selectedColumns: mode === ColumnDisplayMode.Table ? [...selectedColumns, column.name] : selectedColumns,
    inlineColumns: mode === ColumnDisplayMode.Inline ? [...inlineColumns, column.name] : inlineColumns
  };
};

export const sanitizeColumnsDisplay = (
  display: ColumnsDisplay,
  availableColumns: TableColumn[]
): ColumnsDisplay | null => {
  const availableNames = new Set(map(availableColumns, "name"));
  const lockedNames = new Set(map(filter(availableColumns, "locked"), "name"));

  const selectedColumns = uniq([
    ROOT_COLUMN_KEY,
    ...filter(display.selectedColumns, (name) => name !== ROOT_COLUMN_KEY && availableNames.has(name))
  ]);
  const inlineColumns = uniq(
    filter(
      display.inlineColumns,
      (name) => availableNames.has(name) && !lockedNames.has(name) && !includes(selectedColumns, name)
    )
  );

  const sanitizedDisplay = { selectedColumns, inlineColumns };
  const currentDisplay = { selectedColumns: display.selectedColumns, inlineColumns: display.inlineColumns };
  return isEqual(sanitizedDisplay, currentDisplay) ? null : sanitizedDisplay;
};

// "roles" columns are shared between both Projects roots, so their changes are reflected on each other
export const getCalendarRootsToSync = (
  calendarRoot: CalendarRoot,
  columnsType: ColumnsType
): CalendarRoot[] => {
  switch (calendarRoot) {
    case CalendarRoot.WorkforceProfiles:
      return [CalendarRoot.WorkforceProfiles];
    case CalendarRoot.ProjectsEngagements:
      return columnsType === "roles"
        ? [CalendarRoot.ProjectsEngagements, CalendarRoot.ProjectsRoles]
        : [CalendarRoot.ProjectsEngagements];
    case CalendarRoot.ProjectsRoles:
      return columnsType === "roles"
        ? [CalendarRoot.ProjectsRoles, CalendarRoot.ProjectsEngagements]
        : [CalendarRoot.ProjectsRoles];
  }
};
